/**
 * The lead form, shared by the Leads list (a new lead) and the lead page (editing one), so the two
 * cannot come to disagree about what a lead holds. Status is not on it: a status change is recorded
 * on the timeline, so it goes through the lead page's own buttons.
 */
import { TextArea } from 'webwin';
import { describeError } from '../../api/http.js';
import * as marketing from '../../api/marketing.js';
import { LeadSource, type LeadDto, type UserDto } from '../../contracts.gen.js';
import { LEAD_SOURCES, parseTags, sourceLabel } from '../../leads.js';
import { isoDayOf, middayUtcFromIso } from '../../recurrence.js';
import type { Card } from '../../ui/Card.js';
import { dateBox } from '../../ui/DateBox.js';
import { moneyBox } from '../../ui/MoneyBox.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, field, fieldRow, GAP, h2, muted, relayout, stackOf, textBox, vstack } from '../../ui/layout.js';

export interface LeadEditor {
  readonly card: Card;
  /** Opens blank, or filled in from `lead`. */
  open(lead?: LeadDto): void;
  close(): void;
  setUsers(users: readonly UserDto[]): void;
}

export function leadEditor(options: { onSaved: (lead: LeadDto, created: boolean) => void; cancelled: () => boolean }): LeadEditor {
  let editingId = 0;
  let busy = false;
  let users: readonly UserDto[] = [];

  const title = h2('New lead');
  const company = textBox();
  const contact = textBox();
  const email = textBox({ placeholder: 'name@company.com' });
  const phone = textBox();
  const website = textBox();
  const address = new TextArea({ height: 60, hAlign: 'stretch', wrap: true });
  const source = comboBox<string>(LEAD_SOURCES.map(sourceLabel), { selectedIndex: 0 });
  const sourceDetail = textBox({ placeholder: 'Who referred them, which event…' });
  const monthly = moneyBox();
  const owner = comboBox<string>(['Nobody'], { selectedIndex: 0 });
  const followUp = dateBox();
  const tags = textBox({ placeholder: 'Comma-separated, e.g. dental, 20-50 seats' });
  const notes = new TextArea({ height: 90, hAlign: 'stretch', wrap: true });
  const status = new StatusLine();

  const shell = card({ visible: false });
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      title,
      fieldRow(field('Company', company), field('Contact name', contact)),
      fieldRow(field('Email', email), field('Phone', phone)),
      fieldRow(field('Website', website), field('Estimated monthly value', monthly.row)),
      field('Address', address),
      fieldRow(field('Source', source), field('Source detail', sourceDetail)),
      fieldRow(field('Owner', owner), field('Next follow-up', followUp.picker)),
      field('Tags', tags),
      field('Notes', notes),
      muted('A lead needs a company or a contact name. Everything else can be filled in as you learn it.'),
      stackOf(buttonRow(), button('Save', () => void save(), true), button('Cancel', () => close())),
      status.label
    )
  );

  function ownerIndexOf(id: number | null): number {
    return id === null ? 0 : Math.max(0, users.findIndex(u => u.id === id) + 1);
  }

  function open(lead?: LeadDto): void {
    editingId = lead?.id ?? 0;
    title.text = lead ? 'Edit lead' : 'New lead';
    // LiteDB reads a stored "" back as null, whatever the contract says.
    company.text = lead?.company ?? '';
    contact.text = lead?.contactName ?? '';
    email.text = lead?.email ?? '';
    phone.text = lead?.phone ?? '';
    website.text = lead?.website ?? '';
    address.text = lead?.address ?? '';
    source.selectedIndex = Math.max(0, LEAD_SOURCES.indexOf(lead?.source ?? LeadSource.Manual));
    sourceDetail.text = lead?.sourceDetail ?? '';
    monthly.set(lead && lead.estimatedMonthlyValue > 0 ? lead.estimatedMonthlyValue : null);
    owner.selectedIndex = ownerIndexOf(lead?.ownerUserId ?? null);
    followUp.set(lead?.nextFollowUpUtc ? isoDayOf(lead.nextFollowUpUtc) : null);
    tags.text = (lead?.tags ?? []).join(', ');
    notes.text = lead?.notes ?? '';
    status.clear();
    shell.visible = true;
    relayout(shell);
    company.focus();
  }

  function close(): void {
    shell.visible = false;
    relayout(shell.parent ?? shell);
  }

  async function save(): Promise<void> {
    if (busy) return;
    if (!company.text.trim() && !contact.text.trim()) {
      status.error('Give the lead a company or a contact name.');
      return;
    }
    const value = monthly.value();
    if (value !== null && value < 0) {
      status.error('The estimated monthly value cannot be negative.');
      return;
    }
    const body = {
      company: company.text.trim(),
      contactName: contact.text.trim(),
      email: email.text.trim(),
      phone: phone.text.trim(),
      website: website.text.trim(),
      address: address.text.trim(),
      source: LEAD_SOURCES[source.selectedIndex] ?? LeadSource.Manual,
      sourceDetail: sourceDetail.text.trim(),
      estimatedMonthlyValue: value ?? 0,
      ownerUserId: owner.selectedIndex > 0 ? (users[owner.selectedIndex - 1]?.id ?? null) : null,
      tags: parseTags(tags.text),
      nextFollowUpUtc: middayUtcFromIso(followUp.iso())?.toISOString() ?? null,
      notes: notes.text.trim()
    };
    busy = true;
    status.busy('Saving…');
    try {
      const created = editingId === 0;
      const lead = created ? await marketing.createLead(body) : await marketing.updateLead(editingId, body);
      if (options.cancelled()) return;
      status.clear();
      close();
      options.onSaved(lead, created);
    } catch (error) {
      if (!options.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
    }
  }

  return {
    card: shell,
    open,
    close,
    setUsers(next) {
      users = next.filter(u => !u.disabled);
      const selected = owner.selectedIndex;
      owner.items = ['Nobody', ...users.map(u => u.displayName)];
      owner.selectedIndex = Math.min(selected, users.length);
    }
  };
}
